import { Check } from '@mui/icons-material';
import { CircularProgress, Grid } from '@mui/material';
import { validateEmail, validatePhoneNumber } from '@nx-smartmonkey/shared/helpers';
import {
  Checkbox,
  HSpacer,
  RoutalDialog,
  RoutalPalette,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  RoutalTooltip,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import WarningIcon from '../assets/svg/WarningIcon';
import FileCopy from '../assets/svg/dispatch-icons/copy-link.svg';
import EmailIcon from '../assets/svg/dispatch-icons/email.svg';
import OpenInNew from '../assets/svg/dispatch-icons/open-tab.svg';
import WhatsAppIcon from '../assets/svg/dispatch-icons/whatsapp.svg';
import { RouteAvatar } from '../components/RouteAvatar';
import * as routing from '../constants/Routing';
import { Driver } from '../domain/driver/Driver';
import { useRetrieveActivePlanDrivers } from '../hooks/plan/useRetrieveActivePlanDrivers';
import { useRetrieveActiveProject } from '../hooks/project/useRetrieveActiveProject';
import { useRetrieveUserCredentials } from '../hooks/user/useRetrieveUserCredentials';
import { useSearchVehicles } from '../hooks/vehicle/useSearchVehicles';
import Button from '../react-components/Button';
import Text from '../react-components/Text';
import configuration from '../services/configuration';
import eventsPanel from '../services/events';
import { dispatchRouteByEmail } from '../services/requests';

const getActionIconSrc = ({
  act,
  size = 24,
  disabled = false,
  onClick,
}: {
  act: ActionTypes;
  size?: number;
  disabled?: boolean;
  onClick?: (...args: any[]) => any;
}) => {
  let Icon;
  switch (act) {
    case `copy_link`:
      Icon = FileCopy;
      break;
    case `send_email`:
      Icon = EmailIcon;
      break;
    case `send_whatsapp`:
      Icon = WhatsAppIcon;
      break;
    case `open_tab`:
      Icon = OpenInNew;
      break;
    default:
      return null;
  }
  return (
    <img
      style={{
        cursor: disabled ? `default` : `pointer`,
        opacity: disabled ? `0.5` : `1`,
      }}
      onClick={() => {
        if (!disabled && onClick) {
          onClick();
        }
      }}
      width={size}
      height={size}
      src={Icon}
      alt=""
      draggable="false"
    />
  );
};

type DispatchDialogProps = {
  handleCloseDialog: (...args: any[]) => any;
  dispatchDialogOpen: boolean;
};

type ActionTypes = `send_whatsapp` | `send_email` | `copy_link` | `open_tab`;

export const DispatchDialog = ({ handleCloseDialog, dispatchDialogOpen }: DispatchDialogProps) => {
  const navigate = useNavigate();
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: userCredentials } = useRetrieveUserCredentials();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const [localDrivers, setLocalDrivers] = useState<Driver[]>(planDrivers ?? []);
  const [sendToAllEmptyEmails, setSendToAllEmptyEmails] = useState<Driver[]>([]);
  const [copyFlags, setCopyFlags] = useState<string[]>([]);
  const [sendFlags, setSendFlags] = useState<string[]>([]);
  const [sendCurrentRoute, setSendCurrentRoute] = useState<boolean>(false);
  const [action, setAction] = useState<ActionTypes>(`copy_link`);
  const { data: paginatedVehicles } = useSearchVehicles({
    offset: 0,
    limit: 100,
  });

  useEffect(() => {
    if ((planDrivers ?? []).length > 0) {
      setLocalDrivers(planDrivers!);
    }
    const retrievedSendCurrentRoute = localStorage.getItem(`DISPATCH_CURRENT_ROUTE`);
    setSendCurrentRoute(Boolean(retrievedSendCurrentRoute ?? false));

    const retrievedAction = localStorage.getItem(`DISPATCH_ACTION`);
    setAction(
      `${
        retrievedAction && [`copy_link`, `send_email`, `send_whatsapp`, `open_tab`].includes(retrievedAction)
          ? retrievedAction
          : `copy_link`
      }` as ActionTypes
    );

    return () => {
      setLocalDrivers([]);
    };
  }, [planDrivers]);

  useEffect(() => {
    if (localDrivers && localDrivers.length > 0) {
      setSendToAllEmptyEmails(
        localDrivers.filter((driver) => !driver.email || driver.email === `` || !validateEmail(driver.email))
      );
    }
  }, [localDrivers]);

  if (!activeProject) {
    return null;
  }

  const generateLink = (driver: Driver) => {
    if (sendCurrentRoute) {
      return driver.getRoutePublicLink();
    }
    return configuration.DRIVERS_ENDPOINT;
  };

  const handleCopyLink = (driver: Driver) => {
    if (driver) {
      const url = generateLink(driver);
      const el = document.createElement(`textarea`);
      el.value = url;
      const helper = document.getElementById(`helper_copy_assign`);
      if (!helper) return;

      helper.appendChild(el);
      el.select();
      document.execCommand(`copy`);
      helper.removeChild(el);

      eventsPanel.planDispatchAction(`dispatch_copy_link`);
    }
  };

  const handleViewLink = (driver: Driver) => {
    eventsPanel.planDispatchAction(`dispatch_open_link`);
    window.open(generateLink(driver), `_blank`);
  };

  const handleSendEmail = async (driverId: string) => {
    setSendFlags([...sendFlags, driverId]);

    sendDriverEmail(driverId).finally(() => {
      setSendFlags([...sendFlags.filter((flag) => flag !== driverId)]);
    });

    eventsPanel.planDispatchAction(`dispatch_send_email`);
  };

  const sendDriverEmail = async (driverId: string) => {
    return dispatchRouteByEmail(driverId).catch((error) => {
      console.error(`error`, error);
    });
  };

  const sendAllEmails = () => {
    const validDrivers = localDrivers.filter(
      (driver) => driver.email && driver.email !== `` && validateEmail(driver.email)
    );
    if (validDrivers.length > 0) {
      const loadingDrivers = [...sendFlags, ...validDrivers.map((driver) => driver.id)];

      setSendFlags(loadingDrivers);

      validDrivers.forEach((driver) => {
        sendDriverEmail(driver.id).finally(() => {
          setSendFlags(sendFlags.filter((flag: any) => flag !== driver.id));
        });
      });

      eventsPanel.planDispatchAction(`dispatch_send_email_to_all`);
    }
  };

  const getDriverLinkedVehiclePhone = (driver: Driver) => {
    const vehicle = (paginatedVehicles?.docs ?? []).find((v) => v.id === driver.vehicle_id);
    if (vehicle?.phone && validatePhoneNumber(vehicle.phone)) {
      return vehicle.phone;
    }
    return ``;
  };

  return (
    <RoutalDialog
      id="dispatch-dialog"
      open={dispatchDialogOpen}
      dialogProps={{
        style: {
          minHeight: `400px`,
        },
      }}
      onCloseDialog={() => {
        setLocalDrivers(planDrivers ?? []);
        setSendToAllEmptyEmails([]);
        setCopyFlags([]);
        setSendFlags([]);
        handleCloseDialog();
      }}
      titleContent={<Text variant="h6" intlId="dispatch_dialog.title" />}
      footerContent={
        <div style={{ width: `100%`, marginBottom: `8px` }}>
          {action === `send_email` ? (
            <>
              {activeProject.view?.vehicle.email ? (
                <Grid container direction="column">
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item sm={12} md={sendToAllEmptyEmails.length !== localDrivers.length ? 8 : 12}>
                      <Grid container alignItems="center" direction="row" style={{ flexWrap: `unset` }}>
                        {sendToAllEmptyEmails.length > 0 && (
                          <WarningIcon style={{ color: RoutalPalette.danger, fontSize: `24px`, marginRight: `8px` }} />
                        )}

                        {sendToAllEmptyEmails.length === 0 ? (
                          <Text intlId="dispatch_dialog.send_email_to_all_info" />
                        ) : sendToAllEmptyEmails.length === localDrivers.length ? (
                          <Text intlId="dispatch_dialog.no_drivers_with_valid_email" />
                        ) : (
                          <Text
                            intlId="dispatch_dialog.send_email_to_all_error"
                            intlValues={{
                              drivers: sendToAllEmptyEmails.map((driver) => driver.label).join(`, `),
                            }}
                          />
                        )}
                      </Grid>
                    </Grid>

                    {sendToAllEmptyEmails.length !== localDrivers.length && (
                      <Grid item sm={12} md={4}>
                        <Grid container direction="row" justifyContent="flex-end">
                          <Button
                            onClick={() => {
                              sendAllEmails();
                            }}
                            disabled={sendFlags.length > 0}
                            intlId={
                              sendToAllEmptyEmails.length === 0
                                ? `dispatch_dialog.button_send_email_to_all`
                                : `dispatch_dialog.button_send_email_to_all_anyway`
                            }
                          />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              ) : null}

              {!activeProject.view?.vehicle.email ? (
                <Grid container direction="column">
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item sm={12} md={sendToAllEmptyEmails.length !== localDrivers.length ? 8 : 12}>
                      <Grid container alignItems="center" direction="row" style={{ flexWrap: `unset` }}>
                        <WarningIcon style={{ color: RoutalPalette.danger, fontSize: `24px`, marginRight: `8px` }} />

                        <HSpacer small />

                        <div style={{ fontSize: `14px` }}>
                          {userCredentials?.planner.projects.test(activeProject.id, `can_update_project`) ? (
                            <>
                              <Text tagName="span" intlId="dispatch_dialog.user_view_email_not_enabled.1" />

                              <Text
                                tagName="span"
                                variant="button"
                                onClick={() => {
                                  navigate(
                                    `${routing.PROJECT_SETTINGS.PLANNER_FIELDS.VEHICLES.replace(
                                      `:projectId`,
                                      activeProject.id
                                    )}`
                                  );
                                }}
                                intlId="dispatch_dialog.user_view_email_not_enabled.2"
                              />
                            </>
                          ) : (
                            <Text tagName="span" intlId="dispatch_dialog.user_view_email_not_enabled.not_privileged" />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ) : null}
            </>
          ) : null}
        </div>
      }
    >
      <div id="helper_copy_assign" />

      <Grid
        item
        container
        direction="row"
        wrap="nowrap"
        alignItems="center"
        justifyContent="space-between"
        style={{
          padding: `10px`,
          borderRadius: `8px`,
          backgroundColor: `#EEEEEE`,
        }}
      >
        <Checkbox
          checked={sendCurrentRoute}
          onChange={(e: any, value: any) => {
            setSendCurrentRoute(value);
            localStorage.setItem(`DISPATCH_CURRENT_ROUTE`, value);
          }}
        />
        <Grid item container direction="row" wrap="nowrap" alignItems="center">
          <HSpacer small />
          <Text tagName="span" intlId="dispatch_dialog.send_current_route" />
        </Grid>
      </Grid>

      <VSpacer medium />

      <RoutalSelect
        id="action-select"
        style={{
          width: `240px`,
          backgroundColor: RoutalPalette.white,
        }}
        popoverProps={{
          options: [`copy_link`, `send_email`, `send_whatsapp`, `open_tab`].map(
            (act: any): RoutalSelectMultiSelectOption => ({
              key: `action-${act}`,
              // listItemStyles: {
              //   width: `258px`,
              // },
              content: (
                <Grid container direction="row" alignItems="center">
                  {getActionIconSrc({ act, size: 18 })}
                  <HSpacer small />
                  <Text style={{ cursor: `pointer` }} intlId={`dispatch_dialog.action.${act}`} />
                </Grid>
              ),
              onClick: () => {
                localStorage.setItem(`DISPATCH_ACTION`, act);
                setAction(act);
              },
            })
          ),
        }}
      >
        <Grid container direction="row" alignItems="center" style={{ paddingLeft: `6px` }}>
          {getActionIconSrc({ act: action, size: 18 })}
          <HSpacer small />
          <Text style={{ cursor: `pointer` }} intlId={`dispatch_dialog.action.${action}`} />
        </Grid>
      </RoutalSelect>

      <VSpacer medium />

      <div
        style={{
          maxHeight: `400px`,
          overflowY: `auto` as any,
          overflowX: `hidden` as any,
        }}
      >
        <Grid container direction="column">
          {localDrivers.map((driver: Driver, idx: number, driversArray: Driver[]) => {
            return (
              <Grid
                key={`assign-driver-${driver.id}`}
                container
                direction="row"
                alignItems="center"
                style={{
                  padding: `8px 0`,
                  paddingRight: `8px`,
                  borderTop: `1px solid rgba(224, 224, 224, 1)`,
                  ...(idx === localDrivers.length - 1 ? { borderBottom: `1px solid rgba(224, 224, 224, 1)` } : {}),
                }}
                wrap="nowrap"
              >
                <Grid item container direction="column">
                  <Grid item container direction="row" alignItems="center" wrap="nowrap">
                    <RouteAvatar driver={driver} planDrivers={planDrivers} />

                    <HSpacer small />

                    <RoutalTooltip
                      mouseEnterDelay={0.5}
                      mouseLeaveDelay={0}
                      id={driver.id}
                      title={driver.label ?? `No name`}
                      placement="top"
                    >
                      {driver.label && driver.label !== `` ? (
                        <Text variant="body" isBold>
                          {driver.label}
                        </Text>
                      ) : (
                        <Text variant="label" intlId="no_name" style={{ color: RoutalPalette.secondary00 }} />
                      )}
                    </RoutalTooltip>
                  </Grid>
                </Grid>

                <HSpacer medium />

                <Grid item>
                  <Grid container justifyContent="flex-end" wrap="nowrap">
                    {activeProject.view?.vehicle.phone && action === `send_whatsapp` ? (
                      <RoutalTooltip
                        title={
                          <Text
                            style={{ color: `white` }}
                            intlId={
                              getDriverLinkedVehiclePhone(driver) !== ``
                                ? `dispatch_dialog.button_send_whatsapp`
                                : `dispatch_dialog.not_valid_phone`
                            }
                          />
                        }
                      >
                        {getActionIconSrc({
                          act: `send_whatsapp`,
                          size: 22,
                          disabled: getDriverLinkedVehiclePhone(driver) === ``,
                          onClick: () => {
                            eventsPanel.planDispatchAction(`dispatch_send_whatsapp`);
                            const url = encodeURIComponent(generateLink(driver));
                            window.open(
                              `https://api.whatsapp.com/send?phone=${getDriverLinkedVehiclePhone(driver)}&text=${url}`,
                              `_blank`
                            );
                          },
                        })}
                      </RoutalTooltip>
                    ) : null}

                    {activeProject.view?.vehicle.email && action === `send_email` ? (
                      <RoutalTooltip
                        title={
                          <Text
                            style={{ color: `white` }}
                            intlId={
                              !sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``
                                ? `dispatch_dialog.not_valid_phone`
                                : `dispatch_dialog.button_send_email${
                                    !driver.email || !validateEmail(driver.email) ? `_disabled` : ``
                                  }`
                            }
                          />
                        }
                      >
                        <div>
                          {!sendFlags.includes(driver.id) && (
                            <>
                              {getActionIconSrc({
                                act: `send_email`,
                                size: 22,
                                disabled:
                                  (!sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``) ||
                                  !driver.email ||
                                  !validateEmail(driver.email),
                                onClick: () => {
                                  handleSendEmail(driver.id);
                                },
                              })}
                            </>
                          )}
                          {sendFlags.includes(driver.id) && <CircularProgress size={24} />}
                        </div>
                      </RoutalTooltip>
                    ) : null}

                    {action === `copy_link` ? (
                      <RoutalTooltip
                        title={
                          <Text
                            style={{ color: `white` }}
                            intlId={
                              !sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``
                                ? `dispatch_dialog.not_valid_phone`
                                : `dispatch_dialog.button_copy_link`
                            }
                          />
                        }
                      >
                        <div>
                          {!copyFlags.includes(driver.id) && (
                            <>
                              {getActionIconSrc({
                                act: `copy_link`,
                                size: 22,
                                disabled: !sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``,
                                onClick: () => {
                                  // setCopyIconDriverId(driver.id);
                                  setCopyFlags([...copyFlags, driver.id]);
                                  handleCopyLink(driver);
                                  setTimeout(() => {
                                    setCopyFlags(copyFlags);
                                  }, 3000);
                                },
                              })}
                            </>
                          )}
                          {copyFlags.includes(driver.id) && (
                            <Check
                              style={{
                                color: RoutalPalette.success,
                                fontSize: `24px`,
                              }}
                            />
                          )}
                        </div>
                      </RoutalTooltip>
                    ) : null}

                    {action === `open_tab` ? (
                      <RoutalTooltip
                        title={
                          <Text
                            style={{ color: `white` }}
                            intlId={
                              !sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``
                                ? `dispatch_dialog.not_valid_phone`
                                : `dispatch_dialog.button_open_link`
                            }
                          />
                        }
                      >
                        {getActionIconSrc({
                          act: `open_tab`,
                          size: 22,
                          disabled: !sendCurrentRoute && getDriverLinkedVehiclePhone(driver) === ``,
                          onClick: () => {
                            handleViewLink(driver);
                          },
                        })}
                      </RoutalTooltip>
                    ) : null}
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </Grid>
      </div>
    </RoutalDialog>
  );
};
